import type { RequestCategory } from "@nlc/shared-types";

import { Link, useSearch } from "@tanstack/react-router";
import { SearchIcon, Sparkles } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { useWhitelabel } from "@/hooks/useWhitelabel";
import { MBC_FLAG } from "@/lib/posthog-flags";

import type { DesignType, SubcategoryWithIcon } from "../types";

import boostModalImage from "../assets/boost-modal.png";
import LayersIcon from "../assets/layers.svg?react";
import LockIcon from "../assets/lock.svg?react";
import { designTypeMatchesQuery, normalizeSearchQuery } from "../utils";
import { DesignTypesCarousel } from "./DesignTypesCarousel";
import { SearchButton } from "./SearchButton";
import { UpgradeModal } from "./UpgradeModal";

type DesignTypeCardProps = {
  designType: DesignType;
};

function DesignTypeCard({ designType }: DesignTypeCardProps) {
  const { ["design-type"]: selectedDesignType } = useSearch({
    from: "/_auth/_app-shell/requests/pick-type",
  });
  const { isWhitelabel } = useWhitelabel();
  const mbcEnabled = useFeatureFlagEnabled(MBC_FLAG) === true;
  const imageUrl = isWhitelabel
    ? (designType.whitelabelPlaceholderImageUrl ??
      designType.placeholderImageUrl)
    : designType.placeholderImageUrl;

  const cardClassName = twMerge(
    "relative h-[214px] w-[222px] shrink-0 cursor-pointer overflow-hidden rounded-sm border border-border-secondary transition-all duration-150 ease-in-out hover:border-utility-brand-400",
    selectedDesignType === designType.id &&
      "outline outline-2 outline-utility-brand-600",
    "focus-visible:outline focus-visible:outline-4 focus-visible:outline-utility-brand-600/20 focus-visible:border-utility-brand-400",
    "active:outline active:outline-2 active:outline-utility-brand-600",
  );

  const opensMbc = mbcEnabled && designType.isMbcCompatible;

  const cardContent = (
    <div className="relative flex h-full flex-col items-center gap-sm">
      {opensMbc && (
        <span className="absolute right-2 top-2 z-10 flex items-center gap-1 whitespace-nowrap rounded-full bg-utility-brand-600 px-2 py-0.5 text-xs font-medium text-white shadow-sm">
          <Sparkles className="size-3" />
          MBC 2.0 compatible
        </span>
      )}
      <div className="w-full flex-1 min-h-0 bg-bg-type-card-background">
        <img className="w-full h-full p-4" src={imageUrl ?? undefined} />
      </div>
      {designType.description && (
        <div className="absolute bottom-[53px] rounded-full border border-utility-gray-200 bg-utility-gray-50 px-md py-xxs text-xs font-medium text-utility-gray-700">
          {designType.description}
        </div>
      )}
      <div className="h-5xl bg-bg-primary text-md font-medium text-text-secondary">
        {designType.name}
      </div>
    </div>
  );

  // MBC-compatible types open the Magical Brief Creator instead of the
  // standard request-create form.
  if (opensMbc) {
    return (
      <Link
        className={cardClassName}
        search={(prev) => ({
          designType: designType.id,
          ["project-id"]: prev["project-id"],
          ["request-type"]: designType.requestType,
        })}
        to="/requests/mbc"
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <Link
      className={cardClassName}
      search={(prev) => ({
        designType: designType.id,
        ["project-id"]: prev["project-id"],
        ["request-type"]: designType.requestType,
      })}
      to="/requests/create"
    >
      {cardContent}
    </Link>
  );
}

type SubcategoryPanelProps = {
  designTypes: DesignType[];
  subcategory: SubcategoryWithIcon;
};

function SubcategoryPanel({ designTypes, subcategory }: SubcategoryPanelProps) {
  const subcategoryDesignTypes = designTypes.filter(
    (type) => type.subcategoryId === subcategory.id,
  );

  return (
    <div className="flex max-lg:flex-none lg:flex-1 lg:min-h-0 flex-col max-lg:overflow-y-visible lg:overflow-y-auto overflow-x-hidden pt-xs lg:touch-pan-y lg:overscroll-contain">
      <div className="lg:hidden">
        <DesignTypesCarousel
          designTypes={subcategoryDesignTypes}
          subcategoryName={subcategory.name}
        />
      </div>
      <div className="hidden lg:flex lg:flex-col lg:gap-lg lg:px-3xl lg:pb-2xl">
        <div className="flex h-5xl items-center">
          <span className="text-lg font-semibold text-text-primary">
            {subcategory.name}
          </span>
        </div>
        <div className="flex flex-wrap gap-[10px]">
          {subcategoryDesignTypes.map((type) => (
            <DesignTypeCard designType={type} key={type.id} />
          ))}
        </div>
      </div>
    </div>
  );
}

type SubcategoryContentProps = {
  categoryId: RequestCategory;
  designTypes: DesignType[];
  onClearSearch: () => void;
  searchQuery: string;
  subcategories: SubcategoryWithIcon[];
};

function SubcategoryContent({
  categoryId,
  designTypes,
  onClearSearch,
  searchQuery,
  subcategories,
}: SubcategoryContentProps) {
  const categorySubcategories = subcategories.filter(
    (sub) => sub.category === categoryId,
  );

  const normalizedQuery = normalizeSearchQuery(searchQuery);

  const filteredTypes = designTypes.filter(
    (type) =>
      designTypeMatchesQuery(type, normalizedQuery) &&
      categorySubcategories.some((sub) => sub.id === type.subcategoryId),
  );

  const filteredSubcategories = categorySubcategories.filter((subcategory) => {
    const types = filteredTypes.filter(
      (type) => type.subcategoryId === subcategory.id,
    );

    return types.length > 0;
  });

  if (normalizedQuery && filteredTypes.length === 0) {
    return (
      <div className="flex max-lg:min-h-[40vh] lg:flex-1 lg:min-h-0 items-center justify-center">
        <div className="flex flex-col items-center gap-3xl">
          <div className="flex flex-col items-center text-center">
            <div className="mb-xl flex rounded-lg border border-featured-icon-modern-border p-lg">
              <SearchIcon size={24} />
            </div>
            <span className="mb-xs text-md font-semibold text-text-primary">
              No type request found
            </span>
            <span className="text-sm text-text-tertiary">
              Your search did not match any type request.
            </span>
            <span className="text-sm text-text-tertiary">
              Please try again.
            </span>
          </div>
          <Button onPress={onClearSearch} size="lg">
            Clear search
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex max-lg:flex-none lg:flex-1 lg:min-h-0 flex-col gap-lg max-lg:overflow-y-visible lg:overflow-y-auto overflow-x-hidden pb-2xl pt-xs lg:touch-pan-y lg:overscroll-contain [scrollbar-gutter:stable]">
        {filteredSubcategories.map((subcategory) => {
          const subcategoryDesignTypes = filteredTypes.filter(
            (type) => type.subcategoryId === subcategory.id,
          );

          if (subcategoryDesignTypes.length === 0) return null;

          return (
            <DesignTypesCarousel
              designTypes={subcategoryDesignTypes}
              key={subcategory.id}
              subcategoryName={subcategory.name}
            />
          );
        })}
      </div>
    </>
  );
}

type SubcategoryTabsProps = {
  categoryId: RequestCategory;
  designTypes: DesignType[];
  onClearSearch: () => void;
  onSearchChange: (value: string) => void;
  onSubcategoryChange: (key: string) => void;
  searchQuery: string;
  subcategories: SubcategoryWithIcon[];
  subcategory: string;
};

export function SubcategoryTabs({
  categoryId,
  designTypes,
  onClearSearch,
  onSearchChange,
  onSubcategoryChange,
  searchQuery,
  subcategories,
  subcategory,
}: SubcategoryTabsProps) {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  const categorySubcategories = subcategories.filter(
    (sub) => sub.category === categoryId,
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 max-lg:flex-none">
      <div className="shrink-0 lg:hidden px-xl pt-lg pb-sm">
        <div className="flex h-[36px] items-center gap-sm rounded-lg border border-border-primary bg-bg-primary px-md">
          <SearchIcon className="size-2xl shrink-0 text-fg-quaternary" />
          <input
            className="w-full bg-transparent text-md text-text-primary outline-none placeholder:text-text-placeholder"
            onChange={handleSearchChange}
            placeholder="Search a request type..."
            type="text"
            value={searchQuery}
          />
        </div>
      </div>
      <div className="flex flex-col flex-1 min-h-0 max-lg:flex-none">
        <div className="shrink-0 flex items-center gap-sm px-xl lg:px-3xl pt-sm lg:pt-lg pb-sm lg:justify-between">
          <div className="flex flex-wrap gap-sm lg:max-w-[960px]">
            <Button
              className="shrink-0"
              color={subcategory === "0" ? "color" : "gray"}
              onPress={() => onSubcategoryChange("0")}
              size="sm"
              variant="secondary"
            >
              <LayersIcon className="shrink-0 size-2xl" />
              All
            </Button>
            {categorySubcategories.map((sub) => {
              if (sub.isBlocked) {
                return (
                  <UpgradeModal
                    bodyText="Take your plan to the next level by unlocking additional capabilities.  Activate these new features right away at a prorated cost, or wait until your next renewal."
                    headerText="Ready to boost your plan with new features?"
                    heroImage={boostModalImage}
                    key={sub.id}
                    onConfirm={() => {
                      window.open(
                        "https://nlc.com/manage-my-account/",
                        "_blank",
                      );
                    }}
                  >
                    <Button
                      className="shrink-0"
                      color={
                        subcategory === sub.id.toString() ? "color" : "gray"
                      }
                      size="sm"
                      variant="secondary"
                    >
                      <LockIcon className="shrink-0 size-2xl" />
                      {sub.name}
                    </Button>
                  </UpgradeModal>
                );
              }

              return (
                <Button
                  className="shrink-0"
                  color={subcategory === sub.id.toString() ? "color" : "gray"}
                  key={sub.id}
                  onPress={() => onSubcategoryChange(sub.id.toString())}
                  size="sm"
                  variant="secondary"
                >
                  {sub.icon && <sub.icon className="shrink-0 size-2xl" />}
                  {sub.name}
                </Button>
              );
            })}
          </div>
          <div className="shrink-0 hidden lg:block lg:self-start">
            <SearchButton onSearch={handleSearchChange} value={searchQuery} />
          </div>
        </div>
        {subcategory === "0" ? (
          <SubcategoryContent
            categoryId={categoryId}
            designTypes={designTypes}
            onClearSearch={onClearSearch}
            searchQuery={searchQuery}
            subcategories={subcategories}
          />
        ) : (
          categorySubcategories.map(
            (sub) =>
              subcategory === sub.id.toString() && (
                <SubcategoryPanel
                  designTypes={designTypes}
                  key={sub.id}
                  subcategory={sub}
                />
              ),
          )
        )}
      </div>
    </div>
  );
}
